import {DataGrid, gridClasses} from "@mui/x-data-grid";
import React, {useEffect, useMemo, useState} from "react";
import {Button} from "react-bootstrap";
import {FiDownload} from "react-icons/fi";
import {useListContext} from "../../contexts/ListContext";
import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import {usePostEnrichmentDownload} from "../../hooks/useDownloadData";
import {useEntrezMap} from "../../hooks/useEntrezMap";
import {usePostEnrichment} from "../../hooks/useJsonLoader";
import {AlertType, DataType} from "../../types/enums";

import {Correlations, EnrichmentData, EnrichmentResult, Scores} from "../../types/list";
import DrawerSelect from "../DrawerSelect";
import {useEnrichmentColumns} from "./ListTableColumns";

const enrichmentMap: Record<string, string> = {
  go_bp: "GO BP",
  go_cc: "GO CC",
  go_mf: "GO MF",
  msigdb: "MSigDB",
  ebi_complex: "EBI Complex",
  reactome: "Reactome",
};

const ListEnrichments = () => {
  const {currentGene, addAlert, removeAlert} = useTheCellMapContext();
  const {listState} = useListContext();
  const entrezMap = useEntrezMap();

  const enrichmentPost = usePostEnrichment();
  const enrichmentDownload = usePostEnrichmentDownload();
  const [data, setData] = useState<EnrichmentResult | undefined>(undefined);
  const [enrichmentType, setEnrichmentType] = useState(Object.keys(enrichmentMap)[0]);

  const {enrichmentColumns} = useEnrichmentColumns();

  /**
   * Memoized payload for the Enrichment API request, derived from the current table data and selected gene.
   */
  const enrichmentPostData = useMemo<EnrichmentData>(() => {
    if (!listState.displayedTableData || !currentGene || !entrezMap) {
      return {data: [], enrichment_type: enrichmentType};
    }

    // Guard against stale data from a different table shape (e.g., rapid data type switches)
    if (listState.dataType === DataType.Correlations) {
      const rows = (listState.displayedTableData as Correlations).correlations ?? [];
      const data = rows.map((row) => ({name: row.g2_lbl})); // For PCC g1 is always the selected gene, use g2_lbl
      return {data, enrichment_type: enrichmentType};
    }

    const rows = (listState.displayedTableData as Scores).scores ?? [];
    const data = rows.flatMap((row) => {
      if (+row.gene_entrez === currentGene.value) {
        /** Our selected gene is library gene in this row, use screen gene */
        return row.screen_entrez.split(",").map((entrez) => ({ // Screen can represent multiple genes
          name: entrezMap[+entrez].s,
        }));
      }
      /** Our selected gene is screen gene in this row, use screen library gene */
      return {name: entrezMap[+row.gene_entrez].s};
    });

    return {
      data,
      enrichment_type: enrichmentType,
    };
  }, [listState.displayedTableData, listState.dataType, currentGene, entrezMap, enrichmentType]);

  useEffect(() => {
    if (!enrichmentPostData.data.length) {
      setData({enrichment: []});
      return
    }

    let isCurrent = true;
    setData(undefined);

    enrichmentPost(enrichmentPostData, false)
      .then(result => {
        if (!isCurrent) return; // Drop stale responses
        setData(result ?? {enrichment: []});
      });

    return () => {
      isCurrent = false;
    };
  }, [enrichmentPostData, enrichmentPost]);

  const handleEnrichmentDownload = () => {
    if (!enrichmentPostData.data.length) return;

    const alert = {
      type: AlertType.Info,
      message: "Download of Enrichment data will start shortly.",
    };

    addAlert(alert);
    enrichmentDownload(enrichmentPostData).finally(() => removeAlert(alert));
  };

  return (<>
      <div className="d-flex flex-row h-100 w-100">
        <div className="d-flex flex-column align-items-center flex-shrink-0">
          <div className="d-flex flex-column align-items-center w-100">
            <Button
              variant="outline-primary"
              size="sm"
              className="d-flex flex-column align-items-center justify-content-center border-0 w-100 py-2 border-tr-0 border-br-0"
            >
              <FiDownload
                title="Download enrichment data"
                onClick={handleEnrichmentDownload}
              />
            </Button>
          </div>

          <DrawerSelect selectOptions={enrichmentMap} onSelect={setEnrichmentType}/>
        </div>
        <div className="flex-fill h-100" style={{minWidth: 0}}>
          <DataGrid
            autoHeight={false}
            className="border-0"
            style={{width: '100%', height: '100%'}}
            columns={enrichmentColumns}
            rows={data?.enrichment || []}
            getRowId={(row) => row.id}
            disableColumnMenu
            hideFooter
            hideFooterPagination
            rowHeight={27}
            localeText={{
              noRowsLabel: !currentGene ? 'Search genes to view enrichments.'
                : data ? 'No significant enrichment results.' : 'Loading...',
            }}
            sx={{
              [`& .${gridClasses.columnHeaders}`]: {
                borderTopLeftRadius: 0,
              },
              [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                outline: "transparent",
              },
              [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: {
                outline: "none",
              },
              [`& .${gridClasses.row}`]: {
                cursor: "default",              // no pointer cursor
                pointerEvents: "none",          // blocks clicks
              },
            }}
          />
        </div>
      </div>
    </>
  )
}
export default ListEnrichments;
